import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const EcommerceApi: React.FC = () => {
  return (
    <>
      <SEO
        title="MyBank - Ecommerce API Integration Guide"
        description="Complete guide for integrating MyBank's N-Genius Online payment gateway into your ecommerce platform"
        keywords={[
          'ecommerce api',
          'payment integration',
          'online payments',
          'payment gateway',
          'ecommerce api',
          'payment integration',
          'online payments',
          'payment gateway',
          'ecommerce api',
          'payment integration',
          'online payments',
          'payment gateway',
          'ecommerce api',
          'payment integration',
          'online payments',
          'payment gateway',
        ]}
      />
      
      {/* Hero Section with Background Image */}
      <div 
        className="relative bg-cover bg-center py-20" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 31, 95, 0.9), rgba(0, 31, 95, 0.9)), url(${'/assets/images/bank.webp'})`,
          minHeight: '400px'
        }}
      >
        <div className="container mx-auto px-4 flex flex-col items-center justify-center">
          <div className=" gap-2 text-sm text-white/80 mb-4">
            <Link to="/" className="hover:text-[#017EFF]">Home</Link>
            <span>-</span>
            <span>API Documentation</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Hosted Payment Page Integration
          </h1>
          <p className="text-xl text-white/90 max-w-2xl">
            Simple, secure and PCI DSS compliant payment solution for your business. Accept payments from customers worldwide with our easy-to-integrate, mobile-first payment pages.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Overview</h2>
            <p className="text-gray-700 mb-4">
              A hosted payment page integration is the most common implementation method for small and medium-sized businesses looking to accept payments online. This solution offers:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Competitive level of branding and customization</li>
              <li>Peace-of-mind with PCI DSS compliance</li>
              <li>Secure payment processing on Network International controlled pages</li>
              <li>Mobile-first, responsive design</li>
              <li>Support for multiple currencies and payment methods</li>
            </ul>
          </section>

          {/* API Key Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Getting Started</h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <p className="text-blue-700">
                Before proceeding, ensure you have:
              </p>
              <ul className="list-disc list-inside mt-2 text-blue-600">
                <li>N-Genius Online Portal login credentials</li>
                <li>Service Account API key</li>
                <li>Outlet reference for your trading outlet</li>
              </ul>
            </div>
          </section>

          {/* Authentication */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Authentication</h2>
            <h3 className="text-xl font-semibold text-gray-700 mb-3">Request an Access Token</h3>
            <p className="text-gray-700 mb-4">
              The first step is to obtain an access token that expires after 5 minutes. This token is required for all subsequent API calls.
            </p>
            
            {/* API Details */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="mb-4">
                <span className="font-mono bg-[#001F5F] text-white px-3 py-1 rounded">POST</span>
                <span className="ml-3 font-mono text-gray-700">
                  https://api-gateway.sandbox.mybank.ngenius-payments.com/identity/auth/access-token
                </span>
              </div>
              
              {/* Headers */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Headers:</h4>
                <div className="bg-white rounded border overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Header</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 font-mono text-sm">Content-Type</td>
                        <td className="px-6 py-4 font-mono text-sm">application/vnd.ni-identity.v1+json</td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 font-mono text-sm">Authorization</td>
                        <td className="px-6 py-4 font-mono text-sm">Basic your_api_key</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Code Example */}
              <div>
                <h4 className="font-semibold mb-2">PHP Example:</h4>
                <pre className="bg-gray-900 text-gray-100 rounded p-4 overflow-x-auto">
                  {`$apikey = 'your_api_key';
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "https://api-gateway.sandbox.mybank.ngenius-payments.com/identity/auth/access-token");
curl_setopt($ch, CURLOPT_HTTPHEADER, array(
    "accept: application/vnd.ni-identity.v1+json",
    "authorization: Basic ".$apikey,
    "content-type: application/vnd.ni-identity.v1+json"
));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, 1);
$output = json_decode(curl_exec($ch));
$access_token = $output->access_token;`}
                </pre>
              </div>
            </div>
          </section>

          {/* Create Order Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Creating an Order</h2>
            <p className="text-gray-700 mb-4">
              Once you have an access token, you can create orders to process payments. Each payment requires an order for tracking and management.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="mb-4">
                <span className="font-mono bg-[#001F5F] text-white px-3 py-1 rounded">POST</span>
                <span className="ml-3 font-mono text-gray-700">
                  https://api-gateway.sandbox.mybank.ngenius-payments.com/transactions/outlets/[outlet-reference]/orders
                </span>
              </div>

              {/* Request Parameters */}
              <div className="mb-6">
                <h4 className="font-semibold mb-2">Request Parameters:</h4>
                <div className="bg-white rounded border overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Example</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="px-4 py-3 font-mono text-sm">action</td>
                        <td className="px-4 py-3 text-sm">Order type</td>
                        <td className="px-4 py-3 font-mono text-sm">"PURCHASE"</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-mono text-sm">amount.currencyCode</td>
                        <td className="px-4 py-3 text-sm">Order currency</td>
                        <td className="px-4 py-3 font-mono text-sm">"AED", "USD", "EUR"</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3 font-mono text-sm">amount.value</td>
                        <td className="px-4 py-3 text-sm">Order amount (in minor units)</td>
                        <td className="px-4 py-3 font-mono text-sm">1000 (= 10.00)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Code Example */}
              <div>
                <h4 className="font-semibold mb-2">PHP Example:</h4>
                <pre className="bg-gray-900 text-gray-100 rounded p-4 overflow-x-auto">
                  {`$postData = new StdClass();
$postData->action = "PURCHASE";
$postData->amount = new StdClass();
$postData->amount->currencyCode = "USD";
$postData->amount->value = 900;
$postData->emailAddress = "customer@test.com";
$postData->merchantOrderReference = "ORDER-123";
$postData->merchantAttributes = new StdClass();
$postData->merchantAttributes->redirectUrl = "https://your-site.com/payment/callback";
$postData->merchantAttributes->skipConfirmationPage = true;

$json = json_encode($postData);
$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, "https://api-gateway.sandbox.mybank.ngenius-payments.com/transactions/outlets/".$outlet."/orders");
curl_setopt($ch, CURLOPT_HTTPHEADER, array(
    "Authorization: Bearer ".$access_token,
    "Content-Type: application/vnd.ni-payment.v2+json",
    "Accept: application/vnd.ni-payment.v2+json"
));
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, 1);
curl_setopt($ch, CURLOPT_POSTFIELDS, $json);
$response = json_decode(curl_exec($ch));`}
                </pre>
              </div>
            </div>
          </section>

          {/* Webhooks Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Webhooks Integration</h2>
            <p className="text-gray-700 mb-4">
              Automate your integration with webhooks to receive real-time payment status updates. Webhooks eliminate the need for manual order management and polling operations.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">Key Events</h3>
              <div className="bg-white rounded border overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm">AUTHORISED</td>
                      <td className="px-4 py-3 text-sm">Payment has been authorized</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm">CAPTURED</td>
                      <td className="px-4 py-3 text-sm">Payment has been captured in full</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm">DECLINED</td>
                      <td className="px-4 py-3 text-sm">Payment was declined by the issuing bank</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-sm">REFUNDED</td>
                      <td className="px-4 py-3 text-sm">Payment has been refunded</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Webhook Handler Example */}
              <div className="mt-6">
                <h4 className="font-semibold mb-2">PHP Webhook Handler Example:</h4>
                <pre className="bg-gray-900 text-gray-100 rounded p-4 overflow-x-auto">
                  {`<?php
// Receive webhook data
$payload = file_get_contents('php://input');
$event = json_decode($payload);

// Verify webhook signature (implement your own security checks)
if (!verifyWebhookSignature($payload)) {
    http_response_code(401);
    exit('Invalid signature');
}

// Process the event
switch($event->eventName) {
    case 'AUTHORISED':
        handleAuthorizedPayment($event->order);
        break;
    case 'CAPTURED':
        handleCapturedPayment($event->order);
        break;
    case 'DECLINED':
        handleDeclinedPayment($event->order);
        break;
    // Handle other events...
}

// Always respond with 200 OK
http_response_code(200);
echo json_encode(['status' => 'success']);`}
                </pre>
              </div>
            </div>
          </section>

          {/* Support Section */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#001F5F] mb-4">Need Help?</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 mb-4">
                If you need assistance with your integration or have any questions, our support team is here to help:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Email: callcenter@mybank.so</li>
                <li>Phone: +252613737777</li>
                <li>Documentation: <a href="#" className="text-blue-600 hover:underline">Full API Reference</a></li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default EcommerceApi; 